import React, { useState, useEffect } from "react";
import CustomModal from "./CustomModal";
import AuthInput from "../Input/AuthInput";
import AddingLightLoader from "../Loaders/AddingLightLoader";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";
import { CreateNonProductInvoiceApi } from "../../ApiRequests";
import AuthBtn from "../Buttons/AuthBtn";
import TypeSelector from "../Selector/TypeSelector";
import { toLocalYmd } from "../../utils/dateTime";

const INVOICE_TYPE_CREDIT = "Credit";
const INVOICE_TYPE_DEBIT = "Debit";

const INVOICE_TYPE_OPTIONS = [
  { label: INVOICE_TYPE_CREDIT },
  { label: INVOICE_TYPE_DEBIT },
];

const userData = JSON.parse(localStorage.getItem("user")) || {};

/**
 * Create non-product invoice for Customer (user_type 1) or Company (user_type 2).
 * invoice_type: 1 Credit, 2 Debit
 */
const AddNonProductInvoiceModal = ({
  Open,
  setOpen,
  userType = 1,
  entity,
  onSuccess,
}) => {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [dateStr, setDateStr] = useState(
    () => toLocalYmd()
  );
  const [invoiceTypeLabel, setInvoiceTypeLabel] = useState(INVOICE_TYPE_CREDIT);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (Open) {
      setAmount("");
      setDesc("");
      setDateStr(toLocalYmd());
      setInvoiceTypeLabel(INVOICE_TYPE_CREDIT);
    }
  }, [Open, entity?._id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!entity?._id) {
      WarningToast("No record selected");
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      WarningToast("Enter a valid amount");
      return;
    }
    if (!invoiceTypeLabel) {
      WarningToast("Select invoice type");
      return;
    }

    const branchId = userData.branchId || entity.branchId;
    if (!branchId) {
      WarningToast("Branch is missing");
      return;
    }

    const invoice_type = invoiceTypeLabel === INVOICE_TYPE_DEBIT ? 2 : 1;
    const payload = {
      user_type: userType,
      invoice_type,
      amount: amt,
      desc: desc.trim(),
      date: dateStr,
      branchId,
    };
    if (userType === 1) payload.customerId = entity._id;
    else payload.companyId = entity._id;

    setLoading(true);
    try {
      const res = await CreateNonProductInvoiceApi(payload);
      if (res.data.success) {
        SuccessToast("Non-product invoice saved");
        setOpen(false);
        if (typeof onSuccess === "function") onSuccess();
      } else {
        ErrorToast(res.data.error?.msg || "Failed");
      }
    } catch (err) {
      ErrorToast(
        err.response?.data?.error?.msg ||
          err.message ||
          "Failed to save invoice"
      );
    }
    setLoading(false);
  };

  const title =
    userType === 1
      ? "Add non-product invoice"
      : "Add company non-product invoice";

  const typeHint =
    invoiceTypeLabel === INVOICE_TYPE_DEBIT
      ? userType === 1
        ? "Debit: payment received — reduces remaining"
        : "Debit: payment to company — reduces payable"
      : userType === 1
        ? "Credit: adds to customer due / cash out"
        : "Credit: adds to company payable / cash in";

  if (!entity?._id) return null;

  return (
    <CustomModal open={Open} setOpen={setOpen}>
      <div className="pb-8">
        <h1 className="w-full text-center font-[700] text-2xl py-6 px-4">
          {title}
        </h1>
        <p className="text-center text-sm text-inkSecondary px-6 mb-4 font-quicksand">
          {entity.name}
        </p>
        <div className="flex flex-col gap-y-3 px-8">
          <TypeSelector
            label="Type"
            activeTab={invoiceTypeLabel}
            setActiveTab={setInvoiceTypeLabel}
            options={INVOICE_TYPE_OPTIONS}
            placeholder="Select type"
          />
          <p className="text-xs text-muted font-quicksand -mt-1 px-1">
            {typeHint}
          </p>
          <AuthInput
            label="Amount (PKR)"
            placeholder="Amount"
            Type="number"
            Value={amount}
            setValue={setAmount}
            Width="w-full"
          />
          <AuthInput
            label="Description"
            placeholder="Optional note"
            Value={desc}
            setValue={setDesc}
            Width="w-full"
          />
          <AuthInput
            label="Date"
            Type="date"
            Value={dateStr}
            setValue={setDateStr}
            Width="w-full"
          />
        </div>
        <div className="flex justify-center items-center gap-x-2 mt-6 px-5">
          {loading ? (
            <AddingLightLoader />
          ) : (
            <>
              <AuthBtn title={"Save"} onSubmit={onSubmit} type={1} />
              <AuthBtn
                title={"Cancel"}
                onSubmit={() => setOpen(false)}
                type={2}
              />
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default AddNonProductInvoiceModal;
